import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ArrowUpward, Clear, Edit, Search, Visibility, LockOpen, Lock, PersonAdd, CloudUpload, FirstPage, LastPage, KeyboardArrowLeft, KeyboardArrowRight, Description } from '@mui/icons-material';
import EmployeeCsvImport from './EmployeeCsvImport';
import EmployeePdfImport from './EmployeePdfImport';
import axios from 'axios';
import { ROUTES } from '../../../routes';
import { API_ENDPOINTS } from '../../../Api/endpoints';

// Utility to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// Custom pagination actions with first/prev/next/last (>>) buttons
function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const isFirstPage = page === 0;
  const isLastPage = page >= Math.ceil(count / rowsPerPage) - 1;

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={isFirstPage}
        aria-label="first page"
        size="small"
      >
        <FirstPage />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={isFirstPage}
        aria-label="previous page"
        size="small"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={isLastPage}
        aria-label="next page"
        size="small"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={isLastPage}
        aria-label="last page"
        size="small"
      >
        <LastPage />
      </IconButton>
    </Box>
  );
}

function EmployeeList({ mode = 'employee' }) {
  const isStakeholder = mode === 'stakeholder';
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Get auth token from localStorage
  const getAuthToken = () => localStorage.getItem('authToken');
  
  // Access location to check for navigation state (notifications)
  const location = useLocation();
  const [notification, setNotification] = useState(null);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  
  // Check for notification in navigation state
  useEffect(() => {
    if (location.state?.notification) {
      setNotification(location.state.notification);
      // Clear the state after showing notification
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch employees from the API
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      // Use the proper employee API endpoint
      const token = getAuthToken();
      console.log('Using auth token:', token ? 'Token exists' : 'No token found');
      
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES.LIST, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          employeeRole: isStakeholder ? 'STAKEHOLDER' : 'EMPLOYEE'
        }
      });
      
      console.log('Employee API response:', response.data);
      
      // Validate employee data before setting state
      const employeeData = response.data.data || [];
      console.log('Employee data count:', employeeData.length);
      
      // Filter out employees with null or undefined IDs
      const validEmployees = employeeData.filter(emp => {
        if (!emp.id) {
          console.warn('Found employee with missing ID:', emp);
          return false;
        }
        return true;
      });
      
      console.log('Valid employees count:', validEmployees.length);
      setEmployees(validEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        setError(`Failed to load employees: ${error.response.status} ${error.response.data.message || ''}`);
      } else {
        setError('Failed to load employees');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStakeholder]);

  // Filter employees based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees);
      return;
    }

    const lowercasedSearch = searchTerm.toLowerCase();
    const filtered = employees.filter(employee =>
      (employee.fullName || '').toLowerCase().includes(lowercasedSearch) ||
      (employee.firstName || '').toLowerCase().includes(lowercasedSearch) ||
      (employee.lastName || '').toLowerCase().includes(lowercasedSearch) ||
      (employee.email || '').toLowerCase().includes(lowercasedSearch) ||
      (employee.position?.positionName || '').toLowerCase().includes(lowercasedSearch) ||
      (employee.department?.departmentName || '').toLowerCase().includes(lowercasedSearch) ||
      (employee.employeeId || '').toLowerCase().includes(lowercasedSearch)
    );
    
    setFilteredEmployees(filtered);
    setPage(0); // Reset to first page on new search
  }, [searchTerm, employees]);

  // Sort employees
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sortedData = [...filteredEmployees].sort((a, b) => {
      // Handle null values gracefully
      const valA = a[key] || '';
      const valB = b[key] || '';
      
      // Use localeCompare for strings (most employee fields)
      if (typeof valA === 'string') {
        return direction === 'asc' ? 
          valA.localeCompare(valB) : 
          valB.localeCompare(valA);
      }
      
      // Handle numeric or date comparisons
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setFilteredEmployees(sortedData);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle opening CSV import modal
  const handleOpenCsvImport = () => {
    setCsvImportOpen(true);
  };

  // Handle opening PDF import modal
  const handleOpenPdfImport = () => {
    setPdfImportOpen(true);
  };
  
  // Handle CSV import success
  const handleCsvImportSuccess = (result) => {
    setCsvImportOpen(false);
    setNotification({
      type: 'success',
      message: `Successfully imported ${result.totalSuccess} employees. ${result.totalErrors > 0 ? `${result.totalErrors} errors occurred.` : ''}`
    });
    fetchEmployees(); // Refresh the employee list
  };

  const handlePdfImportSuccess = (response) => {
    setPdfImportOpen(false);

    const envelope = response?.data || response || {};
    const counts = envelope?.data || {};
    const successCount = counts.successCount ?? 0;
    const failureCount = counts.failureCount ?? 0;
    const totalRecords = counts.totalRecords ?? (successCount + failureCount);

    setNotification({
      type: 'success',
      message: `Successfully imported ${successCount} employees. ${failureCount > 0 ? `${failureCount} errors occurred.` : ''}${totalRecords ? ` (Total: ${totalRecords})` : ''}`
    });
    fetchEmployees();
  };

  // Import functionality will be implemented in future
  // const handleImportEmployees = () => {
  //   navigate(ROUTES.CUSTOMER.EMPLOYEES.IMPORT);
  // };

  const routes = isStakeholder ? ROUTES.CUSTOMER.STAKEHOLDERS : ROUTES.CUSTOMER.EMPLOYEES;
  const entityLabel = isStakeholder ? 'stakeholder' : 'employee';

  const handleViewEmployee = (id) => {
    if (id && id !== 'null' && id !== 'undefined') {
      navigate(routes.DETAIL(id));
    } else {
      console.error(`Invalid ${entityLabel} ID:`, id);
      setError(`Cannot view ${entityLabel}: Invalid ID`);
    }
  };

  const handleEditEmployee = (id) => {
    if (id && id !== 'null' && id !== 'undefined') {
      navigate(routes.EDIT(id));
    } else {
      console.error(`Invalid ${entityLabel} ID:`, id);
      setError(`Cannot edit ${entityLabel}: Invalid ID`);
    }
  };

  const handleActivateLogin = (employee) => {
    const employeeId = employee.uid || employee.id;
    if (employee && employeeId) {
      navigate(`${routes.DETAIL(employeeId)}/activate-login`, {
        state: { employee }
      });
    } else {
      console.error(`Invalid ${entityLabel} data for login activation:`, employee);
      setError(`Cannot activate login: Invalid ${entityLabel} data`);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const renderEmployeeTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead sx={{ backgroundColor: '#004d40' }}>
          <TableRow>
            <TableCell 
              onClick={() => handleSort('employeeId')}
              sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
            >
              ID {sortConfig.key === 'employeeId' && (
                <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
              )}
            </TableCell>
            <TableCell 
              onClick={() => handleSort('fullName')}
              sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Name {sortConfig.key === 'fullName' && (
                <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
              )}
            </TableCell>
            {isStakeholder ? (
              <>
                <TableCell
                  onClick={() => handleSort('employeeRole')}
                  sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Role {sortConfig.key === 'employeeRole' && (
                    <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
                  )}
                </TableCell>
                <TableCell
                  onClick={() => handleSort('ownershipPercentage')}
                  sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Ownership % {sortConfig.key === 'ownershipPercentage' && (
                    <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
                  )}
                </TableCell>
              </>
            ) : (
              <>
                <TableCell
                  onClick={() => handleSort('position')}
                  sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Position {sortConfig.key === 'position' && (
                    <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
                  )}
                </TableCell>
                <TableCell
                  onClick={() => handleSort('department')}
                  sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Department {sortConfig.key === 'department' && (
                    <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
                  )}
                </TableCell>
              </>
            )}
            <TableCell
              onClick={() => handleSort('email')}
              sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Email {sortConfig.key === 'email' && (
                <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
              )}
            </TableCell>
            {!isStakeholder && (
              <TableCell
                onClick={() => handleSort('startDate')}
                sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Start Date {sortConfig.key === 'startDate' && (
                  <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
                )}
              </TableCell>
            )}
            <TableCell 
              onClick={() => handleSort('hasLogin')}
              sx={{ color: '#ffffff', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Login Status {sortConfig.key === 'hasLogin' && (
                <ArrowUpward fontSize="small" className={sortConfig.direction === 'asc' ? '' : 'flipped'} />
              )}
            </TableCell>
            <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : filteredEmployees
          ).map((employee) => (
            <TableRow key={employee.id}>
              <TableCell>{employee.employeeId || '-'}</TableCell>
              <TableCell>{employee.fullName?.toUpperCase() || `${(employee.firstName || '').toUpperCase()} ${(employee.lastName || '').toUpperCase()}`.trim()}</TableCell>
              {isStakeholder ? (
                <>
                  <TableCell>{employee.employeeRole || '-'}</TableCell>
                  <TableCell>{employee.ownershipPercentage || '-'}</TableCell>
                </>
              ) : (
                <>
                  <TableCell>{employee.position?.positionName || '-'}</TableCell>
                  <TableCell>{employee.department?.departmentName || '-'}</TableCell>
                </>
              )}
              <TableCell>{employee.email || '-'}</TableCell>
              {!isStakeholder && <TableCell>{formatDate(employee.startDate)}</TableCell>}
              <TableCell>
                {employee.hasLogin ? (
                  <Tooltip title="Employee has login access">
                    <Box display="flex" alignItems="center">
                      <Lock color="primary" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">Active</Typography>
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title="Employee does not have login access">
                    <Box display="flex" alignItems="center">
                      <LockOpen color="action" fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">Inactive</Typography>
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <IconButton onClick={() => {
                  console.log('Employee data:', employee);
                  console.log('Employee ID:', employee.id);
                  console.log('Employee userId:', employee.userId);
                  // Use employee.uid (EMP-YYYYMMDD-NNNN format) for better readability and consistency
                  handleViewEmployee(employee.uid || employee.id);
                }} size="small" title="View employee details">
                  <Visibility />
                </IconButton>
                <IconButton onClick={() => handleEditEmployee(employee.uid || employee.id)} size="small" title="Edit employee">
                  <Edit />
                </IconButton>
                <Tooltip title="View document profile with versioning">
                  <IconButton 
                    onClick={() => navigate(routes.DOCUMENT_PROFILE(employee.uid || employee.id))}
                    size="small" 
                    color="info"
                  >
                    <Description />
                  </IconButton>
                </Tooltip>
                {!employee.hasLogin && (
                  <Tooltip title="Activate login access for this employee">
                    <IconButton 
                      onClick={() => handleActivateLogin(employee)} 
                      size="small" 
                      color="primary"
                    >
                      <PersonAdd />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {filteredEmployees.length === 0 && (
            <TableRow>
              <TableCell colSpan={isStakeholder ? 6 : 8} align="center">
                No {isStakeholder ? 'stakeholders' : 'employees'} found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25]}
        count={filteredEmployees.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        ActionsComponent={(paginationProps) => (
          <TablePaginationActions
            {...paginationProps}
            count={filteredEmployees.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
          />
        )}
      />
    </TableContainer>
  );

  return (
    <Box p={3}>
      {/* Notification snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification(null)} 
          severity={notification?.type || 'info'} 
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
      
      {/* CSV Import Modal */}
      <EmployeeCsvImport
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onSuccess={handleCsvImportSuccess}
      />

      <EmployeePdfImport
        open={pdfImportOpen}
        onClose={() => setPdfImportOpen(false)}
        onSuccess={handlePdfImportSuccess}
      />
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <Box p={3} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5">{isStakeholder ? 'Stakeholder Management' : 'Employee Management'}</Typography>
              {!isStakeholder && (
                <ButtonGroup>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Description />}
                    onClick={handleOpenPdfImport}
                    sx={{ backgroundColor: '#004d40', '&:hover': { backgroundColor: '#00796b' } }}
                  >
                    Import PDF
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CloudUpload />}
                    onClick={handleOpenCsvImport}
                    sx={{ color: '#004d40', borderColor: '#004d40', '&:hover': { borderColor: '#00796b', backgroundColor: 'rgba(0, 77, 64, 0.04)' } }}
                  >
                    Import CSV
                  </Button>
                </ButtonGroup>
              )}
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <Card>
            <Box p={3} display="flex" alignItems="center">
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by name, email, ID, position, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton onClick={clearSearch} edge="end">
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {}}
                sx={{ ml: 2, height: 56, color: '#004d40', borderColor: '#004d40', '&:hover': { borderColor: '#00796b', backgroundColor: 'rgba(0, 77, 64, 0.04)' } }}
              >
                Search
              </Button>
            </Box>
            
            {loading ? (
              <Box p={3} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box p={3}>
                <Typography color="error">{error}</Typography>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={fetchEmployees}
                  sx={{ mt: 2 }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              renderEmployeeTable()
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EmployeeList;
